import React from 'react';

interface Column<T> {
  header: string;
  accessor: keyof T;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (item: T) => React.ReactNode;
}

interface Action<T> {
  icon?: React.ReactNode;
  onClick: (item: T) => void;
  className?: string;
  render?: (item: T) => React.ReactNode;
}

interface TableListProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: Action<T>[];
  onRowClick?: (item: T) => void;
}

export function TableList<T>({ data, columns, actions, onRowClick }: TableListProps<T>) {
  return (
    <div className="overflow-x-auto">
      {/* Tabela para dispositivos grandes */}
      <table className="min-w-full divide-y divide-gray-200 hidden md:table table-fixed">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                scope="col"
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.width ? '' : 'flex-1'
                }`}
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
            {actions && actions.length > 0 && (
              <th scope="col" className="px-6 py-3 w-20 sticky right-0 bg-gray-50 z-10">
                <span className="sr-only">Ações</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (actions && actions.length > 0 ? 1 : 0)}
                className="px-6 py-4 text-center text-sm text-gray-500"
              >
                Nenhum registro encontrado
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50'}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-6 py-4 text-sm text-gray-900 ${
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                    } break-words`}
                  >
                    {column.render ? column.render(item) : String(item[column.accessor] || '')}
                  </td>
                ))}
                {actions && actions.length > 0 && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 z-10">
                    <div className="flex items-center justify-end space-x-3">
                      {actions.map((action, actionIndex) => (
                        <div
                          key={actionIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(item);
                          }}
                          className={`cursor-pointer ${action.className || ''}`}
                        >
                          {action.render ? action.render(item) : action.icon}
                        </div>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Cards para dispositivos menores */}
      <div className="md:hidden grid grid-cols-1 gap-4">
        {data.length === 0 ? (
          <div className="border rounded-lg p-4 shadow-md bg-white text-center text-gray-500">
            Nenhum registro encontrado
          </div>
        ) : (
          data.map((item, index) => (
            <div key={index} className="border rounded-lg p-4 shadow-md bg-white">
              {columns.map((column) => (
                <div 
                  key={String(column.accessor)} 
                  className="mb-3"
                >
                  <div className="font-medium text-gray-700 mb-1">{column.header}:</div>
                  <div className="pl-2 break-words">
                    {column.render ? column.render(item) : String(item[column.accessor] || '')}
                  </div>
                </div>
              ))}
              {actions && (
                <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200">
                  {actions.map((action, actionIndex) => (
                    <div
                      key={actionIndex}
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(item);
                      }}
                      className={`cursor-pointer p-2 ${action.className || ''}`}
                    >
                      {action.render ? action.render(item) : action.icon}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
} 